import { Box } from '@mui/system';
import { IPost } from '_interfaces/post.interface';
import { IPostComment } from '_interfaces/postComment.interface';
import { IProjectProfiles } from '_interfaces/types/project.types';
import {
  addPostCommentAtom,
  addSelectionsFeedPostAtom,
  deletePostCommentAtom,
  deleteSelectionsFeedPostAtom,
  scheduleFeedSearchAtom,
  selectionsFeedPostsAtom,
  toggleSelectionsFeedPostLikeAtom,
  updatePostCommentAtom,
  updateSelectionsFeedPostAtom,
} from 'atoms';
import ActivityFeed from 'components/activity-feed/ActivityFeed';
import { ICustomMentionData } from 'components/inputs/draft-js-input';
import useToast from 'hooks/useToast';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { FC, useMemo } from 'react';
import { useGetMeQuery } from 'redux-store/api/auth';
import { ISelectionItem } from 'redux-store/api/selections';
import { useAppSelector } from 'redux-store/hooks/useAppSelector';
import { authSelector } from 'redux-store/selector';

interface ISelectionsActivityFeed {
  selectionsItem: ISelectionItem | null;
  profiles: IProjectProfiles[];
}

export const SelectionsActivityFeed: FC<ISelectionsActivityFeed> = ({
  selectionsItem,
  profiles,
}) => {
  const { data: userMe } = useGetMeQuery();
  const showToast = useToast();
  const { companyId } = useAppSelector(authSelector);

  const createPost = useSetAtom(addSelectionsFeedPostAtom(selectionsItem?.id?.toString()));
  const updatePost = useSetAtom(updateSelectionsFeedPostAtom);
  const deletePost = useSetAtom(deleteSelectionsFeedPostAtom);
  const createComment = useSetAtom(addPostCommentAtom);
  const updateComment = useSetAtom(updatePostCommentAtom);
  const deleteComment = useSetAtom(deletePostCommentAtom);
  const togglePostLike = useSetAtom(toggleSelectionsFeedPostLikeAtom);
  const [searchQuery, setSearchQuery] = useAtom(scheduleFeedSearchAtom);

  const posts = useAtomValue(selectionsFeedPostsAtom(selectionsItem?.id?.toString())) || [];

  const handleCreatePost = async (post: Partial<IPost> & { mediaFiles?: File[] }) => {
    try {
      await createPost({
        newPost: { ...post, itemName: selectionsItem.title },
        user: {
          userId: userMe?.id?.toString() || '',
          displayName: `${userMe?.firstName} ${userMe?.lastName}` || '',
          avatarUrl: userMe?.avatar || '',
          role:
            userMe?.roles?.find((role) => role.companyId?.toString() === companyId)?.jobTitle || '',
        },
      });
    } catch (error) {
      showToast({
        type: 'error',
        message: `Error creating post: ${error.message}`,
      });
    }
  };

  const handleUpdatePost = async (post: Partial<IPost> & { mediaFiles?: File[] }) => {
    try {
      await updatePost({ ...post, itemName: selectionsItem.title });
      showToast({
        type: 'success',
        message: 'Post updated successfully',
      });
    } catch (error) {
      showToast({
        type: 'error',
        message: `Error updating post: ${error.message}`,
      });
    }
  };

  const handleDeletePost = async (postId: string) => {
    try {
      await deletePost(postId);
      showToast({
        type: 'success',
        message: 'Post deleted successfully',
      });
    } catch (error) {
      showToast({
        type: 'error',
        message: `Error deleting post: ${error.message}`,
      });
    }
  };

  const handleCreateComment = async (
    comment: Partial<IPostComment> & { mediaFiles?: File[] },
    parentPath: string
  ) => {
    try {
      await createComment({
        data: {
          ...comment,
          createdBy: {
            userId: userMe?.id?.toString() || '',
            displayName: `${userMe?.firstName} ${userMe?.lastName}` || '',
            avatarUrl: userMe?.avatar || '',
          },
        },
        parentPath,
      });
    } catch (error) {
      showToast({
        type: 'error',
        message: `Error creating comment: ${error.message}`,
      });
    }
  };

  const handleUpdateComment = async (comment: Partial<IPostComment> & { mediaFiles?: File[] }) => {
    try {
      await updateComment(comment);
      showToast({
        type: 'success',
        message: 'Comment updated successfully',
      });
    } catch (error) {
      showToast({
        type: 'error',
        message: `Error updating comment: ${error.message}`,
      });
    }
  };

  const handleTogglePostLike = async (postId: string) => {
    try {
      await togglePostLike({
        postId,
        selectionsItemId: selectionsItem?.id?.toString(),
        userId: userMe?.id?.toString(),
      });
    } catch (error) {
      showToast({
        type: 'error',
        message: `Error liking post: ${error.message}`,
      });
    }
  };

  const onSetSearchQuery = (searchQuery: string) => {
    setSearchQuery(searchQuery);
  };

  const mentionOptions: ICustomMentionData[] = useMemo(() => {
    return profiles.map((profile: IProjectProfiles) => {
      return {
        id: profile.id,
        name: `${profile.firstName} ${profile.lastName}`,
        firstName: profile.firstName,
        lastName: profile.lastName,
        avatar: profile.avatar,
        email: profile.email,
      };
    });
  }, [profiles]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid #F3F4F9',
        opacity: 1,
        gap: '40px',
      }}
    >
      <ActivityFeed
        posts={posts}
        searchQuery={searchQuery}
        mentionOptions={mentionOptions}
        onCreatePost={handleCreatePost}
        onUpdatePost={handleUpdatePost}
        onCreateComment={handleCreateComment}
        onDeletePost={handleDeletePost}
        onUpdateComment={handleUpdateComment}
        onTogglePostLike={handleTogglePostLike}
        onSetSearchQuery={onSetSearchQuery}
      />
    </Box>
  );
};
